import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleOAuthCallback } from '../services/auth';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const [debugUrl, setDebugUrl] = useState('');

  useEffect(() => {
    // Capture the full URL for debugging before any processing
    setDebugUrl(window.location.href);

    try {
      const key = handleOAuthCallback();
      if (key) {
        setStatus('success');
        setMessage('Successfully connected your Pollinations account!');
        setTimeout(() => navigate('/generate'), 2000);
      } else {
        setStatus('error');
        setMessage('No authentication key received. Please try connecting again.');
        setTimeout(() => navigate('/settings'), 5000);
      }
    } catch {
      setStatus('error');
      setMessage('Authentication failed. Please try again.');
      setTimeout(() => navigate('/settings'), 5000);
    }
  }, [navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center space-y-4 max-w-md">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto animate-spin" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Connecting to Pollinations...
            </h2>
            <p className="text-gray-500 dark:text-gray-400">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Connected!
            </h2>
            <p className="text-gray-500 dark:text-gray-400">{message}</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Redirecting to Generate page...
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Connection Failed
            </h2>
            <p className="text-gray-500 dark:text-gray-400">{message}</p>
            {debugUrl && (
              <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-left">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Debug URL received:</p>
                <p className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">{debugUrl}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
